import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { SortOrder } from '@modules/common/types/sort-order';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { StitchTypeFilters } from '@modules/knowledge/types/stitch-type-filters';
import { type MessagesListSortField, MessagesListState } from '@modules/messages/types/messages-list-state';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { dateRangeChanges, setDateRangeSilently } from '@modules/common/utils/date-range-filter.util';
import { merge, Subject } from 'rxjs';
import { startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

const defaultSortOrder: Record<MessagesListSortField, SortOrder> = {
  attachmentsCount: SortOrder.DESC,
  date: SortOrder.DESC,
  title: SortOrder.ASC,
  score: SortOrder.DESC,
  sentto: SortOrder.ASC,
  sentfrom: SortOrder.ASC,
};

@Component({
  selector: 'app-messages2-list-context-menu',
  templateUrl: './messages-list-context-menu.component.html',
  styleUrls: ['./messages-list-context-menu.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MessagesListContextMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public state: MessagesListState;
  @Input()
  public withAdvancedSearch: boolean;
  @Input()
  public withScore: boolean;
  @Input()
  public withToSort?: boolean;
  @Input()
  public withFromSort?: boolean;
  @Input()
  public withThreadedView?: boolean;
  @Input()
  public withUnreadFilter?: boolean;

  public innerFromDate: UntypedFormControl = new UntypedFormControl();
  public innerToDate: UntypedFormControl = new UntypedFormControl();
  public dateButtonSelected: 'from' | 'to' = 'from';
  public advancedSearchApplied: boolean;

  private readonly stateChanged = new Subject<void>();
  private readonly alive = new Subject<void>();
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly mailAppStateService = inject(MailAppStateService);

  protected readonly virtualFolder = toSignal(this.mailAppStateService.getVirtualFolder());

  public ngOnInit() {
    dateRangeChanges(this.innerFromDate, this.innerToDate)
      .pipe(takeUntil(this.alive))
      .subscribe(({ from, to }) => {
        if (from?.getTime() !== this.state.from?.getTime() || to?.getTime() !== this.state.to?.getTime()) {
          this.state.from = from;
          this.state.to = to;
          this.state.notifyStateChanged();
        }
      });

    merge(
      this.stateChanged.pipe(
        startWith(void 0),
        switchMap(() => this.state.changes().pipe(startWith(this.state))),
        tap(() => {
          this.innerFromDate.setValue(this.state.from, { emitEvent: false });
          this.innerToDate.setValue(this.state.to, { emitEvent: false });
        }),
      ),
      this.advancedSearchService.getApplied().pipe(
        tap((state) => {
          this.advancedSearchApplied = state;
        }),
      ),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('state' in changes) {
      this.stateChanged.next();
    }
  }

  public ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  public sort(sortBy: MessagesListSortField) {
    let order = this.state.sortOrder;
    let by = this.state.sortBy;

    if (by === sortBy) {
      order = order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    } else {
      by = sortBy;
      order = defaultSortOrder[by] ?? SortOrder.ASC;
    }

    this.state.sortBy = by;
    this.state.sortOrder = order;
    this.state.notifyStateChanged();
  }

  public pinnedOnTop() {
    this.state.pinnedOnTop = !this.state.pinnedOnTop;
    this.state.notifyStateChanged();
  }

  public flaggedOnTop() {
    this.state.flaggedOnTop = !this.state.flaggedOnTop;
    this.state.notifyStateChanged();
  }

  public flag() {
    this.state.flagged = this.state.flagged ? null : true;
    this.state.notifyStateChanged();
  }

  public unread() {
    this.state.unread = this.state.unread ? null : true;
    this.state.notifyStateChanged();
  }

  public pin() {
    this.state.pinned = this.state.pinned ? null : true;
    this.state.notifyStateChanged();
  }

  public stitch(stitch: StitchTypeFilters) {
    this.state.stitchedWith = stitch;
    this.state.notifyStateChanged();
  }

  public today() {
    this.state.today = !this.state.today;
    this.state.notifyStateChanged();
  }

  public threadedView() {
    this.state.threadedView = !this.state.threadedView;
    this.state.notifyStateChanged();
  }

  /** Clears the picked date range — driven by the submenu "Clear" button, as the row's click toggles the popover. */
  public clearSelectedDate(): void {
    if (!this.state.from && !this.state.to) {
      return;
    }

    // Silent: the range is written to the state here in one go, instead of letting each control emit.
    setDateRangeSilently(this.innerFromDate, this.innerToDate, { from: null, to: null });
    this.state.from = null;
    this.state.to = null;
    this.state.notifyStateChanged();
  }
}
